import React, { ReactElement, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { downloadScanDocument } from '../helpers/ScanningHelpers';
import { OverviewMode, ScanRes } from '../types/Scan';
import { User } from '../types/User';

import '../styles/components/ActionsContextMenu.less';

interface Props {
    scan: ScanRes;
    currentUser: User;
    xPos: number;
    yPos: number;
    closeMe: () => void;
    deleteScan: (scan: ScanRes) => void;
    markScan: (scanId: number, isReceived: boolean) => void;
    editScan: (scan: ScanRes) => void;
    mode: OverviewMode;
}

const ActionsContextMenu = ({
    scan,
    currentUser,
    xPos,
    yPos,
    closeMe,
    deleteScan,
    markScan,
    editScan,
    mode
}: Props): ReactElement => {
    const [styles, setStyles] = useState({});
    const userCanMarkReceived: boolean = currentUser.isAdmin ?? false;

    const scanBelongsToUserLocation = useMemo(() => {
        if (mode === 'SCANNING') {
            return scan.locationName === currentUser.locationName;
        } else {
            return scan.destinationName === currentUser.locationName;
        }
    }, [currentUser]);

    useEffect(() => {
        disableScrolling();
        document.addEventListener('click', handleClick, true);

        return function cleanUp() {
            enableScrolling();
            document.removeEventListener('click', handleClick, true);
        };
    }, []);

    useLayoutEffect(() => {
        const el = document.getElementById('actions-menu');
        setStyles({
            top: `${yPos + 10}px`,
            left: el ? `${xPos - el.offsetWidth}px` : `${xPos}px`
        });
    }, []);

    const handleClick = (e: any) => {
        const menu = document.getElementById('actions-menu');
        if (menu && !menu.contains(e.target)) {
            closeMe();
        }
    };

    const disableScrolling = () => {
        const x = window.scrollX;
        const y = window.scrollY;
        window.onscroll = () => {
            window.scrollTo(x, y);
        };
    };

    const enableScrolling = () => {
        window.onscroll = () => {
            console.info('Scroll enabled');
        };
    };

    return (
        <div style={styles} id="actions-menu" className={scan ? 'show-menu' : 'hide-menu'}>
            <ReactTooltip id="action-tip" type="dark" effect="solid" place="left" />
            <ul>
                {mode === 'ARRIVING_LOGGERS' && !scan.received && (
                    <li
                        data-for="mark-disabled"
                        data-tip={!userCanMarkReceived ? 'You need an elevated role for this action' : ''}
                        id={!userCanMarkReceived ? 'no-hover' : ''}
                    >
                        <ReactTooltip type="dark" effect="solid" id="mark-disabled" />
                        <button
                            data-for="action-tip"
                            data-tip={!scanBelongsToUserLocation ? 'Not available on a remote location' : ''}
                            data-testid="mark-as-received"
                            disabled={!userCanMarkReceived || !scanBelongsToUserLocation}
                            onClick={() => markScan(scan.id, true)}
                        >
                            <FontAwesomeIcon icon="check-square" id="green" fixedWidth={true} />
                            Mark as Received
                        </button>
                    </li>
                )}
                {mode === 'ARRIVING_LOGGERS' && scan.received && (
                    <li
                        data-for="unmark-disabled"
                        data-tip={!userCanMarkReceived ? 'You need an elevated role for this action' : ''}
                        id={!userCanMarkReceived ? 'no-hover' : ''}
                    >
                        <ReactTooltip type="dark" effect="solid" id="unmark-disabled" />
                        <button
                            data-for="action-tip"
                            data-tip={!scanBelongsToUserLocation ? 'Not available on a remote location' : ''}
                            data-testid="unmark-as-received"
                            disabled={!userCanMarkReceived || !scanBelongsToUserLocation}
                            onClick={() => markScan(scan.id, false)}
                        >
                            <FontAwesomeIcon icon="times-square" id="gray" fixedWidth={true} />
                            Remove "Received" marking
                        </button>
                    </li>
                )}
                {mode === 'SCANNING' && (
                    <li role="listitem">
                        <button
                            data-for="action-tip"
                            data-tip={!scanBelongsToUserLocation ? 'Not available on a remote location' : ''}
                            data-testid="edit-scan-btn"
                            title="Edit Scan"
                            disabled={!scanBelongsToUserLocation}
                            onClick={() => {
                                editScan(scan);
                                closeMe();
                            }}
                        >
                            <FontAwesomeIcon icon="pen" id="dimgray" fixedWidth={true} />
                            <div>Edit Scan</div>
                        </button>
                    </li>
                )}
                {scan.statusName === 'COMPLETE' && (
                    <>
                        <li role="listitem">
                            <button
                                data-testid="download-logger-specs-btn"
                                title="Download Logger Specification Sheet"
                                onClick={() => {
                                    closeMe();
                                    downloadScanDocument('pdf', 'pdf', scan);
                                }}
                            >
                                <FontAwesomeIcon icon="file-pdf" id="dimgray" fixedWidth={true} />
                                <div>Download Logger Specs</div>
                            </button>
                        </li>
                        <li role="listitem">
                            <button
                                data-testid="download-excel-file-btn"
                                title="Download Excel File"
                                onClick={() => {
                                    closeMe();
                                    downloadScanDocument('excel', 'xlsx', scan);
                                }}
                            >
                                <FontAwesomeIcon icon="file-excel" id="dimgray" fixedWidth={true} />
                                <div>Download Excel File</div>
                            </button>
                        </li>
                    </>
                )}
                {mode === 'SCANNING' && scan.statusName === 'IN_PROGRESS' && (
                    <li>
                        <button
                            data-for="action-tip"
                            data-tip={!scanBelongsToUserLocation ? 'Not available on a remote location' : ''}
                            data-testid="delete-scan-btn"
                            title="Delete Scan"
                            disabled={!scanBelongsToUserLocation}
                            onClick={() => {
                                closeMe();
                                deleteScan(scan);
                            }}
                        >
                            <FontAwesomeIcon icon="trash" id="red" fixedWidth={true} />
                            <span>Delete Scan</span>
                        </button>
                    </li>
                )}
            </ul>
        </div>
    );
};

export default ActionsContextMenu;
